import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { IconButton, InputAdornment, TextField, Typography, lighten } from '@mui/material';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import PaiDotLoader from './pai-dot-loader';

interface Props {
  value: string | number;
  placeholder?: string;
  label?: string;
  ariaLabel?: string;
  variant?: 'background' | 'white';
  loading?: boolean;
  disabled?: boolean;
  type?: React.HTMLInputTypeAttribute;
  min?: number;
  max?: number;
  name?: string;
  autocomplete?: string;
  selectOnFocus?: boolean;
  readonly?: boolean;
  style?: React.CSSProperties;

  error?: boolean;
  errorText?: string;

  /** @deprecated Legacy prop – kept for backward compat, has no visual effect */
  disableBorder?: boolean;
  /** @deprecated Legacy prop – kept for backward compat */
  instruction?: string;
  /** @deprecated Legacy prop – kept for backward compat */
  optional?: boolean;
  /** @deprecated Legacy prop – kept for backward compat */
  rows?: number;
  /** @deprecated Legacy prop – kept for backward compat */
  center?: boolean;
  /** @deprecated Legacy prop – kept for backward compat */
  productGroup?: string;
  /** @deprecated Legacy prop – kept for backward compat */
  placeholderSize?: number;

  onChange?(event: React.ChangeEvent<HTMLInputElement>): void;

  onKeyUp?(): void;

  onKeyDown?(event?: React.KeyboardEvent): void;

  onPaste?(event: any): void;
}

const PaiTextField: React.FC<Props> = ({
  value,
  placeholder,
  label,
  ariaLabel,
  variant = 'white',
  loading,
  disabled,
  type,
  min,
  max,
  name,
  autocomplete,
  selectOnFocus,
  readonly,
  style,
  error,
  errorText,
  onChange,
  onKeyUp,
  onKeyDown,
  onPaste,
}) => {
  const { classes } = useStyles({ variant, error, type, disabled });
  const { t } = useTranslation();

  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleFocusSelect = (event: React.FocusEvent<HTMLInputElement>) => {
    event.target.select();
  };

  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.setAttribute('readonly', 'true');
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.removeAttribute('readonly');
        }
      }, 100);
    }
  }, []);

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.target.setAttribute('readonly', 'true');
    setTimeout(() => event.target.removeAttribute('readonly'), 100);
  };

  return (
    <div style={{ width: style?.width || '100%', maxWidth: style?.maxWidth || undefined }}>
      {!_.isNil(label) ? <Typography className={classes.label}>{label}</Typography> : null}
      <TextField
        ref={inputRef}
        className={classes.textfield}
        InputProps={{
          readOnly: readonly,
          disableUnderline: true,
          classes: { input: classes.fieldInput },
          endAdornment: loading ? (
            <span className={classes.loader}>
              <PaiDotLoader size={'small'} inline={true} />
            </span>
          ) : type === 'password' ? (
            <InputAdornment
              className={classes.passwordAdornment}
              position="end"
              style={{ visibility: value !== 0 && value !== '' ? 'visible' : 'hidden' }}
            >
              <IconButton
                aria-label={t('togglePasswordVisibility')}
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                edge="end"
                disableRipple={true}
              >
                {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
              </IconButton>
            </InputAdornment>
          ) : null,
          inputProps: {
            min: !_.isNil(min) ? min : undefined,
            max: !_.isNil(max) ? max : undefined,
          },
        }}
        variant={'standard'}
        type={type === 'password' ? (showPassword ? 'text' : 'password') : type}
        aria-label={ariaLabel}
        fullWidth={true}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onKeyUp={onKeyUp}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onFocus={autocomplete === 'new-password' || autocomplete === 'one-time-code' ? handleFocus : selectOnFocus ? handleFocusSelect : undefined}
        disabled={disabled}
        name={name}
        autoComplete={autocomplete}
        error={!!error}
        style={style}
      />
      {!!error && !_.isNil(errorText) ? <Typography className={classes.errorText}>{errorText}</Typography> : null}
    </div>
  );
};

const useStyles = makeStyles<{
  variant?: 'background' | 'white';
  error?: boolean;
  type?: React.HTMLInputTypeAttribute;
  disabled?: boolean;
}>()((theme, { variant, error, type, disabled }) => ({
  textfield: {
    border:
      variant !== 'background'
        ? !error
          ? disabled
            ? `1px solid ${theme.palette.action.disabled}`
            : `1px solid ${theme.palette.primary.main}`
          : `1px solid ${theme.palette.error.main}`
        : undefined,
    borderRadius: '10px',
    background: variant === 'background' ? theme.palette.backgrounds.main_new : '#FFF',
    '& .MuiSvgIcon-root': {
      color: theme.palette.primary.main,
      height: '100%',
      top: 0,
      background: !error ? (variant === 'background' ? theme.palette.backgrounds.main_new : '#FFF') : lighten(theme.palette.error.main, 0.9),
    },
  },
  fieldInput: {
    fontSize: theme.typography.pxToRem(15),
    color: disabled ? theme.palette.greys.light : undefined,
    padding: type === 'password' ? '8px 40px 8px 12px' : '8px 12px',
    minHeight: 'calc(39px - 8px - 8px)',
    background: !error ? (variant === 'background' ? theme.palette.backgrounds.main_new : '#FFF') : lighten(theme.palette.error.main, 0.9),
    borderRadius: '10px',
    '&::placeholder': {
      opacity: 0.5,
      fontSize: theme.typography.pxToRem(15),
    },
    '&:focus': {
      '&::placeholder': {
        opacity: 0,
      },
    },
  },
  errorText: {
    fontSize: theme.typography.pxToRem(13),
    marginTop: '6px',
    textAlign: 'right',
    color: theme.palette.error.main,
    padding: '0 6px',
  },
  loader: {
    background: !error ? (variant === 'background' ? theme.palette.backgrounds.main_new : '#FFF') : lighten(theme.palette.error.main, 0.9),
    padding: '0 16px',
  },
  passwordAdornment: {
    background: !error ? '#FFF' : lighten(theme.palette.error.main, 0.9),
    position: 'absolute',
    right: '12px',
    '& .MuiButtonBase-root': {
      background: !error ? '#FFF' : lighten(theme.palette.error.main, 0.9),
    },
    '& .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(20),
      background: !error ? '#FFF' : lighten(theme.palette.error.main, 0.9),
      color: !error ? theme.palette.primary.main : theme.palette.error.main,
      '&:hover': {
        color: theme.palette.secondary.main,
      },
    },
  },
  label: {
    marginBottom: '4px',
    marginLeft: '4px',
    fontSize: theme.typography.pxToRem(14),
  },
}));

export default PaiTextField;
